import React from 'react';

import { fetchServiceAccountApiToken, TokenState } from '@neptune/service-accounts-domain';
import {
  FETCH_STATUS_FAILED,
  FETCH_STATUS_NONE,
  FETCH_STATUS_PENDING,
  FETCH_STATUS_SUCCESS,
} from '@neptune/shared/common-domain';

type UseServiceAccountsTokensProps = {
  organizationName?: string;
};

type TokensMap = Record<string, TokenState | undefined>;

export const useServiceAccountsTokens = ({ organizationName }: UseServiceAccountsTokensProps) => {
  const [tokensMap, setTokensMap] = React.useState<TokensMap>({});

  const updateState = React.useCallback(
    (key: string, value: Partial<TokenState>) => {
      const currentState: TokenState = tokensMap?.[key] || {
        isShown: false,
        fetchStatus: FETCH_STATUS_NONE,
      };
      const newState = {
        ...currentState,
        ...value,
      };
      setTokensMap({
        ...tokensMap,
        [key]: newState,
      });
    },
    [tokensMap, setTokensMap],
  );

  const fetchApiToken = React.useCallback(
    async (serviceAccountId: string) => {
      if (!organizationName) {
        return;
      }

      updateState(serviceAccountId, { fetchStatus: FETCH_STATUS_PENDING });

      try {
        const token = await fetchServiceAccountApiToken(serviceAccountId);
        updateState(serviceAccountId, {
          fetchStatus: FETCH_STATUS_SUCCESS,
          token,
        });
        return token;
      } catch {
        updateState(serviceAccountId, { fetchStatus: FETCH_STATUS_FAILED });
      }
    },
    [organizationName, updateState],
  );

  const getApiToken = React.useCallback(
    async (serviceAccountId: string) => {
      return await fetchApiToken(serviceAccountId);
    },
    [fetchApiToken],
  );

  const toggleApiToken = React.useCallback(
    async (serviceAccountId: string) => {
      const state = tokensMap[serviceAccountId];

      if (!state?.isShown) {
        if (state?.token) {
          updateState(serviceAccountId, { isShown: true });
          return;
        }

        const token = await fetchApiToken(serviceAccountId);
        updateState(serviceAccountId, {
          fetchStatus: FETCH_STATUS_SUCCESS,
          token,
          isShown: true,
        });
        return;
      }

      updateState(serviceAccountId, { isShown: false });
    },
    [tokensMap, updateState, fetchApiToken],
  );

  const resetApiToken = React.useCallback(
    (id: string) => {
      setTokensMap({
        ...tokensMap,
        [id]: undefined,
      });
    },
    [tokensMap],
  );

  React.useEffect(() => {
    setTokensMap({});
  }, [organizationName]);

  return {
    getApiToken,
    resetApiToken,
    toggleApiToken,
    tokensMap,
  };
};
